import { omit } from 'ramda'

import { logger } from '@app/common/logger/logger'

import { AuthSession } from './models'

const AUTH_SESSION_RECORD_KEY = 'authSession'
const LEGACY_PERSISTED_AUTH_KEY = 'persist:auth'

const toAuthSession = (candidate: {
  token?: unknown
  refreshToken?: unknown
}): AuthSession | undefined => {
  const { token, refreshToken } = candidate

  return typeof token === 'string' && typeof refreshToken === 'string'
    ? { token, refreshToken }
    : undefined
}

export const readAuthSessionRecord = (): AuthSession | undefined => {
  const stored = localStorage.getItem(AUTH_SESSION_RECORD_KEY)

  if (!stored) {
    return undefined
  }

  try {
    return toAuthSession(JSON.parse(stored))
  } catch (error) {
    logger.error(`Unable to parse the auth session record: ${error.message}`)

    return undefined
  }
}

export const writeAuthSessionRecord = (session: AuthSession): void => {
  try {
    localStorage.setItem(AUTH_SESSION_RECORD_KEY, JSON.stringify(session))
  } catch (error) {
    logger.error(
      `Failed to write the auth session record with refreshToken ${session.refreshToken.slice(
        -4
      )}: ${error.message}`
    )
  }
}

export const clearAuthSessionRecord = (): void => {
  try {
    localStorage.removeItem(AUTH_SESSION_RECORD_KEY)
  } catch (error) {
    logger.error(`Failed to clear the auth session record: ${error.message}`)
  }
}

const readLegacyPersistedFields = (): Record<string, string> | undefined => {
  const persisted = localStorage.getItem(LEGACY_PERSISTED_AUTH_KEY)

  if (!persisted) {
    return undefined
  }

  try {
    return JSON.parse(persisted)
  } catch (error) {
    logger.error(`Unable to parse the persisted auth slice: ${error.message}`)

    return undefined
  }
}

const parsePersistedField = (field: string | undefined): unknown => {
  try {
    return field === undefined ? undefined : JSON.parse(field)
  } catch {
    return undefined
  }
}

const dropSessionFromLegacyPersistedAuth = (persistedFields: Record<string, string>): void => {
  try {
    localStorage.setItem(
      LEGACY_PERSISTED_AUTH_KEY,
      JSON.stringify(omit(['token', 'refreshToken'], persistedFields))
    )
  } catch (error) {
    logger.error(`Failed to drop the session from the persisted auth slice: ${error.message}`)
  }
}

export const migrateLegacyPersistedSession = (): void => {
  const persistedFields = readLegacyPersistedFields()

  if (!persistedFields) {
    return
  }

  const persistedSession = toAuthSession({
    token: parsePersistedField(persistedFields.token),
    refreshToken: parsePersistedField(persistedFields.refreshToken)
  })

  if (!persistedSession) {
    return
  }

  const nonReduxPersistDedicatedAuthSessionRecord = readAuthSessionRecord()

  if (nonReduxPersistDedicatedAuthSessionRecord) {
    logger.info(
      `Dropping the persisted refreshToken ${persistedSession.refreshToken.slice(
        -4
      )} in favour of the dedicated auth session record`
    )

    dropSessionFromLegacyPersistedAuth(persistedFields)
    return
  }

  logger.info(
    `Migrating the persisted refreshToken ${persistedSession.refreshToken.slice(
      -4
    )} from redux persist sliced into the dedicated auth session record, then dropping it from redux persist`
  )

  writeAuthSessionRecord(persistedSession)
  dropSessionFromLegacyPersistedAuth(persistedFields)
}
